import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Lock, Unlock } from "lucide-react";

export function AttendanceLockBar({ session, center, month, onChange }) {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [ok, setOk] = useState(false);
  const valid = center && center !== "ALL";

  const load = useCallback(async () => {
    if (!valid) { setSt(null); onChange?.(null); return; }
    try {
      const r = await api.post("/attendance-dashboard/lock-status", { token: session.token, month, center });
      setSt(r.data); onChange?.(r.data);
    } catch { setSt(null); onChange?.(null); }
  }, [session.token, month, center, valid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    try {
      await api.post("/attendance-dashboard/lock", { token: session.token, month, center, action, reason, confirm: ok });
      toast.success(action === "unlock" ? "Attendance reopened for correction" : "Attendance relocked");
      setOpen(false); setReason(""); setOk(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!st || st.status === "DRAFT") return null;
  const reopened = st.status === "REOPENED_FOR_CORRECTION";
  return (
    <div className={`rounded-lg border p-3 flex flex-wrap items-center gap-3 ${reopened ? "border-amber-300 bg-amber-50" : "border-destructive/30 bg-destructive/10"}`}
      data-testid="attendance-lock-bar">
      {reopened ? <Unlock className="w-4 h-4 text-amber-700" /> : <Lock className="w-4 h-4 text-destructive" />}
      <Badge variant="outline" data-testid="attendance-lock-bar-status">{st.status.replace(/_/g, " ")}</Badge>
      <span className="text-sm flex-1">
        {reopened ? `Open for correction — ${st.unlocked_by}: “${st.unlock_reason}”. Edit and Save Attendance, then relock.`
          : `Attendance for ${center} · ${month} is locked.${st.can_unlock ? "" : " Ask Super Admin (or an MGT user with Unlock Attendance permission)."}`}
        {st.attendance_changed_since_final && (
          <span className="block text-red-700 font-medium" data-testid="attendance-lock-bar-payroll-notice">
            Attendance has changed after Payroll Version {st.payroll_version || 1} was finalised. Reopen Payroll to create a new revision before updating salary.
          </span>)}
      </span>
      {!reopened && st.can_unlock && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="attendance-unlock-btn">Unlock Attendance</Button>}
      {reopened && <Button size="sm" onClick={() => act("lock")} data-testid="attendance-relock-btn">Relock Attendance</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="attendance-unlock-dialog">
          <DialogHeader><DialogTitle>Unlock Attendance · {center} · {month}</DialogTitle>
            <DialogDescription>Recorded with your user ID and time.{st.payroll_state === "FINAL" && " Payroll is finalised — salary stays frozen until payroll is reopened as a new revision."}</DialogDescription></DialogHeader>
          <Textarea rows={2} placeholder="Reason for unlocking" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="attendance-unlock-reason" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)}
            data-testid="attendance-unlock-confirm" />I confirm this unlock.</label>
          <DialogFooter><Button onClick={() => act("unlock")} disabled={reason.trim().length < 5 || !ok} data-testid="attendance-unlock-submit">Unlock</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
